import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import DataTable from 'datatables.net-react';
import { WkoLoadingSpinner } from '../WkoLoadingSpinner';
const scheduleNcReviewPopoversInitialization = () => {
    window.requestAnimationFrame(() => {
        initializeNcReviewPopovers();
    });
};
const initializeNcReviewPopovers = () => {
    const bootstrap = window.bootstrap;
    if (!bootstrap?.Popover) {
        return;
    }
    document
        .querySelectorAll('#ncReviewTable [data-bs-toggle="popover"]')
        .forEach((element) => {
        bootstrap.Popover?.getOrCreateInstance(element);
    });
};
const showNcReviewPopover = (element) => {
    const bootstrap = window.bootstrap;
    if (!bootstrap?.Popover) {
        return;
    }
    bootstrap.Popover.getOrCreateInstance(element).show?.();
};
const formatDate = (value) => {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleDateString();
};
const buildTimesheetReportUrl = (reportBaseUrl, nc) => {
    const startDate = new Date(nc.NcDate);
    startDate.setDate(startDate.getDate() - 7);
    const endDate = new Date(nc.NcDate);
    endDate.setDate(endDate.getDate() + 7);
    const params = new URLSearchParams({
        vessels: String(nc.VesselId),
        crew: String(nc.CrewId),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    });
    return `${reportBaseUrl}?${params.toString()}`;
};
const getSelectionInputId = (nc) => {
    return `selected-${[nc.VesselId, nc.CrewId, nc.NcDate, nc.NcDetails, nc.NcLevel].join('|')}`;
};
const columns = [
    { data: null, orderable: false, searchable: false },
    { data: 'Crew' },
    { data: 'Rank' },
    { data: 'NcDate' },
    { data: 'NcDetails', width: '40%' },
    { data: 'NcLevel' },
    { data: 'NcDuration' },
    { data: null, orderable: false, searchable: false },
    { data: 'ReviewerName', orderable: false },
];
export const NcReviewMainContent = ({ summary, filters, actions, loading, reportBaseUrl, table, }) => {
    const { vesselName, listDetails, sendViaText } = summary;
    const { showAcknowledged, onShowAcknowledgedChange } = filters;
    const { onOpenConfirm } = actions;
    const { tableRef, displayedRows, selectedAll, onToggleAll, onToggleNc, currentPageSelectableCount, onDraw, } = table;
    const linkBack = useMemo(() => `/Home/NcReviewVesselList${window.location.search}`, []);
    const selectableRowsCount = useMemo(() => displayedRows.filter((nc) => !nc.AlreadyAcknowledged).length, [displayedRows]);
    const slots = useMemo(() => ({
        0: (_data, nc) => {
            if (nc.AlreadyAcknowledged) {
                return _jsx(_Fragment, {});
            }
            return (_jsxs("div", { className: "form-check", children: [_jsx("input", { id: getSelectionInputId(nc), type: "checkbox", className: "form-check-input", checked: nc.IsSelected, onChange: () => onToggleNc(nc) }), _jsx("label", { className: "form-check-label", htmlFor: getSelectionInputId(nc) })] }));
        },
        3: (data, type) => (type === 'sort' ? data : formatDate(data)),
        7: (_data, nc) => (_jsx("div", { className: "text-center", children: _jsx("a", { href: buildTimesheetReportUrl(reportBaseUrl, nc), title: "NC timesheet report", target: "_blank", rel: "noreferrer", className: "btn btn-sm btn-outline-danger", children: _jsx("i", { className: "fa fa-file-pdf-o" }) }) })),
        8: (_data, nc) => {
            if (!nc.AlreadyAcknowledged) {
                return _jsx(_Fragment, {});
            }
            if (!nc.AckComments) {
                return _jsxs("div", { children: [nc.ReviewerName, " on ", formatDate(nc.AckDate)] });
            }
            return (_jsxs(_Fragment, { children: [_jsxs("div", { children: [nc.ReviewerName, " on ", formatDate(nc.AckDate)] }), _jsx("a", { tabIndex: 0, role: "button", "data-bs-toggle": "popover", "data-bs-placement": "top", title: `${nc.ReviewerName} - ${formatDate(nc.AckDate)}`, "data-bs-content": nc.AckComments, "data-bs-trigger": "focus", onClick: (event) => {
                            event.preventDefault();
                            showNcReviewPopover(event.currentTarget);
                        }, onFocus: (event) => {
                            showNcReviewPopover(event.currentTarget);
                        }, children: _jsx("i", { className: "fa fa-comment-o" }) })] }));
        },
    }), [onToggleNc, reportBaseUrl]);
    const tableOptions = useMemo(() => ({
        info: false,
        language: {
            emptyTable: 'No non-conformities to review for the selected period',
            paginate: {
                previous: 'Previous',
                next: 'Next',
            },
        },
        pageLength: 10,
        lengthMenu: [10, 20, 50, 100],
        pagingType: 'simple_numbers',
        searching: false,
        order: [[1, 'asc'], [3, 'desc']],
        drawCallback: () => {
            onDraw();
            scheduleNcReviewPopoversInitialization();
        },
        createdRow: (row, data) => {
            if (data.AlreadyAcknowledged) {
                row.classList.add('text-muted', 'bg-light');
            }
        },
    }), [onDraw]);
    return (_jsx("div", { className: "container", children: _jsxs("div", { className: "card shadow", children: [_jsx("div", { className: "card-header position-relative", children: _jsxs("div", { className: "row align-items-start", children: [_jsx("div", { className: "col", children: _jsx("h2", { children: "Non-conformities review & acknowledgement" }) }), _jsx("div", { className: "col text-end", children: _jsx("h3", { children: _jsx("a", { href: linkBack, children: "< Click here to return to the main page" }) }) })] }) }), _jsxs("div", { className: "card-body", children: [_jsxs("div", { className: "d-flex flex-column flex-lg-row gap-3 mb-3", children: [_jsxs("div", { className: "flex-grow-1", children: [_jsx("h3", { children: vesselName }), _jsx("h3", { children: listDetails }), _jsxs("div", { className: "form-check", children: [_jsx("input", { id: "showAcknowledged", type: "checkbox", className: "form-check-input", checked: showAcknowledged, onChange: (event) => onShowAcknowledgedChange(event.target.checked) }), _jsx("label", { className: "form-check-label", htmlFor: "showAcknowledged", children: "Show acknowledged NCs" })] })] }), _jsxs("div", { className: "flex-shrink-0", children: [_jsxs("button", { type: "button", className: "btn btn-outline-success", onClick: onOpenConfirm, disabled: selectableRowsCount === 0, children: [_jsx("i", { className: "fa fa-check" }), " Acknowledge non-conformities"] }), _jsx("p", { className: "small mt-2 mb-0", children: sendViaText })] })] }), _jsx("div", { children: loading ? (_jsx("div", { className: "d-flex justify-content-center py-5", children: _jsx(WkoLoadingSpinner, {}) })) : (_jsx(DataTable, { ref: tableRef, id: "ncReviewTable", data: displayedRows, columns: columns, slots: slots, className: "table table-sm table-hover align-middle w-100", options: tableOptions, children: _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: _jsxs("div", { className: "form-check", children: [_jsx("input", { id: "allNcs", type: "checkbox", className: "form-check-input", checked: selectedAll, onChange: onToggleAll, disabled: currentPageSelectableCount === 0 }), _jsx("label", { className: "form-check-label", htmlFor: "allNcs" })] }) }), _jsx("th", { children: "Crew member" }), _jsx("th", { children: "Rank" }), _jsx("th", { children: "Date" }), _jsx("th", { style: { width: '40%' }, children: "NC details" }), _jsx("th", { children: "NC Level" }), _jsx("th", { children: "NC duration (hours)" }), _jsx("th", { children: "Timesheet" }), _jsx("th", { children: "Review" })] }) }) })) })] })] }) }));
};
